import { Directive, ElementRef, Input, AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';

import { environment } from '@environment';

import { StaticText } from '@project_app/config/static.text';

import { GtmService } from '@app/core/services/analytics/gtm.service';
import { PlatformService } from '@core/services/util/platform.service';
import { Router } from '@angular/router';

// TODO: replace host with @HostBinding or @HostListener, recommended by [angular](https://v17.angular.io/guide/styleguide#style-06-03) 10/10/24
@Directive({
    selector: '[appDeferImageLoad]',
    host: {
        '(error)': 'loadDefaultImage()',
        '(load)': 'validateImageAspectRatio()'
    },
    standalone: true
})
export class DeferImageLoadDirective implements AfterViewInit, OnChanges {
    @Input() private imageSrc: string;
    @Input() public brandsAlternateImage: boolean;
    @Input() public defaultImage: string;

    staticText = StaticText;

    private tolerance = 0.05; // 5% tolerance
    private _intersectionObserver?: IntersectionObserver;
    private _isDeferImageLoaded: boolean;

    constructor(
        private _element: ElementRef,
        private router: Router,
        private gtmService: GtmService,
        private platformService: PlatformService
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        const newImageSrc = changes.imageSrc?.currentValue;

        if (newImageSrc) {
            if (this._isDeferImageLoaded) {
                this._element.nativeElement.src = newImageSrc;
            } else {
                this.imageSrc = newImageSrc;
            }
        }
    }

    public ngAfterViewInit() {
        if (this.defaultImage) {
            this._element.nativeElement.src = environment.DEPLOY_URL + this.defaultImage;
        } else {
            if (this.brandsAlternateImage) {
                this._element.nativeElement.src = environment.DEPLOY_URL + '/assets/images/alternate_missing.png';
            } else {
                // this._element.nativeElement.src = environment.DEPLOY_URL + '/assets/images/missing.png';
                // since missing image is already preloaded, using it directly
                this._element.nativeElement.src = this.staticText.preloadImage;
            }
        }

        if (this.platformService.isBrowser()) {
            if (!('IntersectionObserver' in window)) {
                this._element.nativeElement.src = this.imageSrc;
            } else if (this.imageSrc) {
                this._intersectionObserver = new IntersectionObserver(this.checkForIntersection.bind(this), {
                    rootMargin: '25px',
                    threshold: 0.01
                });
                (this._intersectionObserver as any).POLL_INTERVAL = 100; // required for unsupported browsers to work with the polyfill
                this._intersectionObserver.observe(<Element>this._element.nativeElement);
            }
        }
    }

    private checkForIntersection(entries: Array<IntersectionObserverEntry>) {
        entries.forEach((entry: IntersectionObserverEntry) => {
            if (this.checkIfIntersecting(entry)) {
                this._element.nativeElement.src = this.imageSrc;
                this._isDeferImageLoaded = true;
                this._intersectionObserver.unobserve(<Element>this._element.nativeElement);
                this._intersectionObserver.disconnect();
            }
        });
    }

    private checkIfIntersecting(entry: IntersectionObserverEntry) {
        return (<any>entry).isIntersecting && entry.target === this._element.nativeElement;
    }

    loadDefaultImage() {
        if (this.defaultImage) {
            this._element.nativeElement.src = environment.DEPLOY_URL + this.defaultImage;
        } else {
            if (this.brandsAlternateImage) {
                this._element.nativeElement.src = environment.DEPLOY_URL + '/assets/images/alternate_missing.png';
            } else {
                this._element.nativeElement.src = environment.DEPLOY_URL + '/assets/images/missing.png';
            }
        }
    }

    validateImageAspectRatio() {
        try {
            const imageElement = this._element.nativeElement as HTMLImageElement;
            if (this.shouldIgnoreImage(imageElement) || this.shouldIgnorePage()) {
                return;
            }

            this.checkImageAspectRatio(imageElement);
        } catch (e) {
            this.logError(e);
        }
    }

    shouldIgnoreImage(imageElement: HTMLImageElement): boolean {
        const images = ['missing/eatclub.png', 'missing/box8.png', 'missing/mojo.png', 'missing.png'];
        return images.some(image => imageElement.src.includes(image));
    }

    shouldIgnorePage(): boolean {
        const pages = ['search'];
        return pages.some(page => this.router.url.includes(page));
    }

    checkImageAspectRatio(imageElement: HTMLImageElement) {
        const serverRatio = imageElement.naturalWidth / imageElement.naturalHeight;
        const renderedRatio = imageElement.clientWidth / imageElement.clientHeight;
        const ratioDifference = Math.abs(serverRatio - renderedRatio);

        if (ratioDifference > this.tolerance) {
            this.gtmService.logEvent('web_analytics', {
                event_action: 'image_aspect_ratio_discrepancy',
                event_label: `${imageElement.src}`,
                value1: `Server ratio: ${serverRatio.toFixed(2)}`,
                value2: `Rendered ratio: ${renderedRatio.toFixed(2)}`,
                value3: `Difference: ${ratioDifference.toFixed(4)}`
            });
        }
    }

    logError(e: any) {
        this.gtmService.logEvent('js_errors', {
            event_label: 'Error during image ratio check:',
            event_value: e
        });
    }
}
